import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderService } from '../../services/api';
import CSVImportModal from '../Shared/CSVImportModal';
import { attachEvidenceFromZip } from '../../utils/bulkEvidenceUpload';

interface Order {
  id: string;
  certificateType: string;
  propertyId: string;
  propertyAddress?: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  partnerId?: string;
  partnerName?: string;
  estimatedCompletionDate?: string;
  notes?: string;
}

interface OrderHistoryProps {
  orders?: Order[];
  onSelectOrder?: (order: Order) => void;
  selectedOrderId?: string;
}

export default function OrderHistory({ orders: propOrders, onSelectOrder, selectedOrderId }: OrderHistoryProps = {}) {
  const navigate = useNavigate();
  const [internalOrders, setInternalOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(!propOrders);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [showBulkImport, setShowBulkImport] = useState(false);

  const orders = propOrders || internalOrders;

  useEffect(() => {
    if (!propOrders) {
      loadOrders();
    }
  }, [propOrders]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await orderService.listOrders();
      const orderList = Array.isArray(res.data?.data) ? res.data.data : res.data?.data?.orders || [];
      
      setInternalOrders(orderList.sort((a: Order, b: Order) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ));
    } catch (err: any) {
      console.error('[OrderHistory] Load failed:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'in_progress':
        return 'blue';
      case 'assigned':
        return 'yellow';
      case 'pending':
        return 'gray';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return '✓';
      case 'in_progress':
        return '⟳';
      case 'assigned':
        return '👤';
      case 'pending':
        return '⏳';
      case 'cancelled':
        return '✕';
      default:
        return '○';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysElapsed = (createdAt: string) => {
    const days = Math.floor(
      (new Date().getTime() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24)
    );
    return days;
  };

  const filteredOrders = orders.filter(
    (order) => filterStatus === 'ALL' || order.status === filterStatus
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-12 w-12 bg-emerald-200 rounded-full mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">
          Order History
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowBulkImport(true)}
            title="Bulk upload existing certificate orders from a CSV"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 font-medium text-sm"
          >
            <i className="fas fa-file-csv"></i> Bulk Upload
          </button>
          <button
            onClick={() => navigate('/certificate-orders/new')}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-medium text-sm md:self-auto self-start"
          >
            <span className="mr-2">+</span>
            New Order
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
          <button
            onClick={loadOrders}
            className="text-red-700 hover:text-red-800 text-sm font-medium mt-2 underline"
          >
            Try Again
          </button>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-4xl mb-3">📋</div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No orders yet</h3>
          <p className="text-gray-600 text-sm mb-6">
            Create your first certificate order to get started
          </p>
          <button
            onClick={() => navigate('/certificate-orders/new')}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-medium"
          >
            Create New Order
          </button>
        </div>
      ) : (
        <>
          {/* Filter Tabs - Mobile Scrollable, Desktop Grid */}
          <div className="mb-6 border-b border-gray-200">
            <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-hide">
              {['ALL', 'pending', 'assigned', 'in_progress', 'completed', 'cancelled'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition ${
                    filterStatus === status
                      ? 'bg-emerald-100 text-emerald-700 border-b-2 border-emerald-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {status === 'ALL' ? 'All Orders' : getStatusLabel(status)}
                  {status !== 'ALL' && (
                    <span className="ml-2 text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                      {orders.filter((o) => o.status === status).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {filteredOrders.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm">
                No {filterStatus === 'ALL' ? '' : filterStatus} orders found
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map((order) => (
                <div
                  key={order.id}
                  onClick={() => {
                    if (onSelectOrder) {
                      onSelectOrder(order);
                    } else {
                      navigate(`/certificate-orders/${order.id}`);
                    }
                  }}
                  className={`border rounded-lg p-4 hover:shadow-md transition cursor-pointer md:p-6 ${
                    selectedOrderId === order.id ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      {/* Certificate Type & ID */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 text-sm md:text-base">
                          {order.certificateType
                            .replace(/_/g, ' ')
                            .split(' ')
                            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(' ')}
                        </h3>
                        <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                          {order.id}
                        </span>
                      </div>

                      {/* Property */}
                      <p className="text-gray-600 text-sm mb-3">
                        {order.propertyAddress || `Property ${order.propertyId}`}
                      </p>

                      {/* Status Badge */}
                      <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `var(--color-${getStatusColor(order.status)}-100, #f3f4f6)`,
                          color: `var(--color-${getStatusColor(order.status)}-700, #374151)`,
                        }}
                      >
                        <span>{getStatusIcon(order.status)}</span>
                        <span>{getStatusLabel(order.status)}</span>
                      </div>

                      {/* Partner Assignment */}
                      {order.partnerName && (
                        <div className="mt-2 text-xs text-gray-600">
                          👤 Assigned to: <span className="font-medium">{order.partnerName}</span>
                        </div>
                      )}
                    </div>

                    {/* Right Side: Dates */}
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm text-gray-900 font-medium mb-1">
                        {formatDate(order.createdAt)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {getDaysElapsed(order.createdAt)} days ago
                      </div>
                      {order.completedAt && (
                        <div className="text-xs text-green-600 font-medium mt-1">
                          Completed: {formatDate(order.completedAt)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <CSVImportModal
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImport={async (data, zipFile) => {
          const VALID_TYPES = ['gas_safety', 'electrical', 'epc', 'cp12', 'eicr'];
          const VALID_STATUSES = ['pending', 'confirmed', 'in_progress', 'completed', 'cancelled'];
          let rows: any[] = data.map((row: any, idx: number) => {
            const certificateType = String(row.certificate_type || '').toLowerCase().trim();
            if (!VALID_TYPES.includes(certificateType)) {
              throw new Error(`Row ${idx + 1}: certificate_type must be one of ${VALID_TYPES.join(', ')}`);
            }
            const status = String(row.status || 'completed').toLowerCase().trim();
            if (!VALID_STATUSES.includes(status)) throw new Error(`Row ${idx + 1}: status must be one of ${VALID_STATUSES.join(', ')}`);
            return {
              propertyId: row.property_id,
              certificateType,
              contractorName: row.contractor_name || '',
              orderDate: row.order_date || undefined,
              status,
              amount: row.amount || undefined,
              notes: row.notes || '',
              evidence_filename: row.evidence_filename || '',
            };
          });
          rows = await attachEvidenceFromZip(rows, zipFile, {
            filenameField: 'evidence_filename',
            resolvePropertyId: (row) => row.propertyId,
          });
          const orders = rows.map(({ evidence_filename, _attachStatus, ...rest }) => rest);
          await orderService.bulkImport(orders);
          if (!propOrders) loadOrders();
        }}
        title="Bulk Upload Certificate Orders"
        headerRequirements={[
          { id: 'property_id', label: 'Property ID', required: true, description: 'The property this order is for', example: 'PROP-0001' },
          { id: 'certificate_type', label: 'Certificate Type', required: true, description: 'gas_safety, electrical, epc, cp12 or eicr', example: 'gas_safety' },
          { id: 'contractor_name', label: 'Contractor Name', required: false, description: 'Who carried out the work', example: 'Acme Gas Ltd' },
          { id: 'order_date', label: 'Order Date', required: false, description: 'YYYY-MM-DD', example: '2026-01-15' },
          { id: 'status', label: 'Status', required: false, description: 'pending, confirmed, in_progress, completed or cancelled (defaults to completed)', example: 'completed' },
          { id: 'amount', label: 'Amount', required: false, description: 'Cost of the order', example: '95.00' },
          { id: 'notes', label: 'Notes', required: false, description: 'Any extra details', example: '' },
          { id: 'evidence_filename', label: 'Evidence Filename', required: false, description: 'Filename inside the companion .zip to attach as this order\'s invoice/certificate', example: 'gas_safety_invoice.pdf' },
        ]}
        templateHeaders={['property_id', 'certificate_type', 'contractor_name', 'order_date', 'status', 'amount', 'notes', 'evidence_filename']}
        sampleData="PROP-0001,gas_safety,Acme Gas Ltd,2026-01-15,completed,95.00,,gas_safety_invoice.pdf"
        allowFileAttachments
      />
    </div>
  );
}
